import React, {Fragment, useEffect, useState} from 'react';
import {Formik} from "formik";
import * as Yup from "yup";
import {debounce, pickBy, cloneDeep} from 'lodash'
import CategorySelector from "../../../../services/categories/CategorySelector";
import client from "../../../../services/session/apiClient";
import {UncontrolledTooltip} from "reactstrap";
import AccountSelector from "../../../../services/accounts/AccountSelector";
import CountrySelector from "../../../../services/categories/CountrySelector";
import GoogleCategorySelector from "../../../../services/categories/GoogleCategorySelector";


const USPhones = ["US","CA"];
const usPhoneRegex=/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/; //used for US and Canada
const outsideUS=/\d+/; //used for outside the US

const getPhoneRegex = (maybeCountry) => (!!maybeCountry && USPhones.includes(maybeCountry)) ? usPhoneRegex : outsideUS;

const baseSchema = (maybeCountry) => {
  const phoneRegex = getPhoneRegex(maybeCountry);
  return {
  businessName: Yup.string()
    .max(200, 'Business Name must be 200 characters or less.'),
    primaryWebsite: Yup.string()
    .url("Primary Website must be a valid url."),
  alternateWebsite: Yup.string(),
  appointmentUrl: Yup.string(),
  primaryPhoneNumber: Yup.string().matches(phoneRegex, "Main Phone must be a valid phone number."),
  alternatePhoneNumber: Yup.string(),
  state: Yup.string(),
  city: Yup.string(),
  postalCode: Yup.string(),
  street: Yup.string(),
  suite: Yup.string(),
  description: Yup.string()
    .min(200, 'Business Description must be between 200 and 750 characters.')
    .max(750, 'Business Description must be between 200 and 750 characters.'),
  businessOwner: Yup.string(),
  businessEmail: Yup.string().email("Business Email must be a valid email."),
  faxNumber: Yup.string().matches(phoneRegex, "Business Fax must be a valid phone number."),
  yearEstablished: Yup.number().test('len', 'Year Established must be a 4-digit year.', val => val === undefined || val.toString().length === 4)
  }
};

export const generalTabLocationFields = [
  "businessName",
  "primaryWebsite",
  "alternateWebsite",
  "appointmentUrl",
  "primaryPhoneNumber",
  "alternatePhoneNumber",
  "primaryCategory",
];

export const generalTabListingManagementFields = [
  "description",
  "businessOwner",
  "businessEmail",
  "faxNumber",
  "yearEstablished",
  "paymentTypes",
];

export const generalTabBusinessAddressFields = [
  "country",
  "state",
  "city",
  "postalCode",
  "street",
  "suite",
];

const paymentTypes = ["American Express", "Visa", "MasterCard", "Discover", "Cash", "Check"];

function BulkLocationEditorGeneralDetails ({
  forceGeneralDetailsValidation,
  selectAccount,
  createMode,
  setFormIsInvalid,
  accountsStore,
  singleLocationMode,
  combinedLocationServicesMap,
  locationPatch,
  mergeIntoLocationPatch,
  categoriesStore
}) {

  // If we're in single location mode, make some fields required
  // Unfortunately there isn't a pretty way to do this (like conditionally making fields required)

  const phoneRegex = getPhoneRegex(!!locationPatch.businessAddress && locationPatch.businessAddress.country);

  const schemaWithRequiredFieldsSet = singleLocationMode ? {
    ...baseSchema(!!locationPatch.businessAddress && locationPatch.businessAddress.country),
    businessName: Yup.string()
      .max(200, 'Business Name must be 200 characters or less.')
      .required("Business Name is required."),
    primaryPhoneNumber: Yup.string().matches(phoneRegex, "Main Phone must be a valid phone number.")
      .required("Main Phone is required."),
    state: Yup.string().required("State/Province is required."),
    city: Yup.string().required("City is required."),
    postalCode: Yup.string().required("Postal Code is required."),
    street: Yup.string().required("Street Address is required."),
    description: Yup.string()
      .min(200, 'Business Description must be between 200 and 750 characters.')
      .max(750, 'Business Description must be between 200 and 750 characters.')
      .required("Business Description is required.")
  } : baseSchema(!!locationPatch.businessAddress && locationPatch.businessAddress.country);

  const schemaWithRequiredLLMFieldsSet = (singleLocationMode && combinedLocationServicesMap.listingManagement) ? {
    ...schemaWithRequiredFieldsSet,
    primaryWebsite: Yup.string().url("Primary Website must be a valid url.")
      .required("Primary Website is necessary for Listing Management accounts."),
  } : schemaWithRequiredFieldsSet;

  const BulkLocationEditorGeneralDetailsSchema = Yup.object().shape(schemaWithRequiredLLMFieldsSet);

  // Location root fields
  const locationFields = {};
  generalTabLocationFields.forEach(field => {
    if (field === "alternateWebsite") {
      // Alternate websites stored as an array
      locationFields[field] = locationPatch.alternateWebsites[0];
    } else if (field === "alternatePhoneNumber") {
      // Alternate phones stored as an array
      locationFields[field] = locationPatch.alternatePhoneNumbers[0];
    } else {
      locationFields[field] = locationPatch[field];
    }
  });
  const listingManagementFields = {};
  generalTabListingManagementFields.forEach(field => {
    listingManagementFields[field] = locationPatch.listingManagementSpecs[field];
  });

  const businessAddressFields = {};
  generalTabBusinessAddressFields.forEach(field => {
    businessAddressFields[field] = locationPatch.businessAddress[field];
  });

  const [primaryCategoryAlternate, setPrimaryCategoryAlternate] = useState(null);

  const [formErrors, setFormErrors] = useState({});

  const initialValues = {
    ...locationFields,
    ...listingManagementFields,
    ...businessAddressFields
  };

  let formInitialized = false;

  const handlePrimaryCategoryUpdate = (primaryCategory) => {
    setPrimaryCategoryAlternate(null);
    if (primaryCategory === undefined) {
      return mergeIntoLocationPatch({primaryCategory})
    }
    if (!combinedLocationServicesMap.listingManagement) {
      // No validation needed if the location is not in a listing management account

      // Don't allow navigation to other tabs if there are form errors like missing required fields
      const formIsInvalid = isFormValid(formErrors, !!locationPatch.accountId, true);
      setFormIsInvalid(!formIsInvalid);

      return mergeIntoLocationPatch({primaryCategory})
    }
    return client.get("/locations/keywords/validForListingManagement", {
      params: {
        keyword: primaryCategory
      }
    }).then(resp => {
      if (resp.data.isValid) {
        // Don't allow navigation to other tabs if there are form errors like missing required fields
        const formIsInvalid = isFormValid(formErrors, !!locationPatch.accountId, true);
        setFormIsInvalid(!formIsInvalid);

        mergeIntoLocationPatch({primaryCategory})
      } else {
        // Don't allow navigation to other tabs if there are form errors like missing required fields
        const formIsInvalid = isFormValid(formErrors, !!locationPatch.accountId, false);
        setFormIsInvalid(!formIsInvalid);

        const alternate = resp.data.alternate;
        setPrimaryCategoryAlternate(alternate);
      }
    });
  };


  const handleCountryUpdate = (country) => {
    mergeIntoLocationPatch({
      businessAddress: {
        country
      }
    })
  };

  const _selectAccount = (selectedAccountId) => {
    selectAccount(selectedAccountId); // When we change accounts we want to recalulate the tabs that should be displayed in the location editor.
    mergeIntoLocationPatch({accountId: selectedAccountId})
  };

  const isFormValid = (formErrors, accountIdSet, primaryCategorySet) => {
    const requiresDescriptionValidation = combinedLocationServicesMap.listingManagement;

    const formErrorsCopy = {...formErrors};
    if (!requiresDescriptionValidation) {
      delete formErrorsCopy["description"];
    }

    return Object.keys(formErrorsCopy).length === 0 &&
      (!singleLocationMode || (accountIdSet && primaryCategorySet))
  };

  const setAppointmentUrl = (e) => {
    const appointmentUrl = e.target.value;
    if (appointmentUrl === "") {
      mergeIntoLocationPatch({
        "listingManagementSpecs": {
          "appointmentUrl": null
        }
      })
    } else {
      mergeIntoLocationPatch({
        "listingManagementSpecs": {
          "appointmentUrl": appointmentUrl
        }
      })
    }
  };

  const handleFormUpdate = debounce((formValues, touched, errors) => {
    setFormErrors(errors);
    // Don't allow navigation to other tabs if there are form errors like missing required fields
    const formIsInvalid = isFormValid(errors, !!locationPatch.accountId, !!locationPatch.primaryCategory);
    setFormIsInvalid(!formIsInvalid);

    if (formInitialized) {

      // Root location fields
      const formLocationFields = cloneDeep(pickBy(formValues, (value, key) => {
        return !errors[key] && !!touched[key] && (locationFields.hasOwnProperty(key) || key === "alternateWebsite")
      }));
      // Need to map alternate websites to an array
      if (!!formLocationFields.alternateWebsite) {
        formLocationFields.alternateWebsites = [formLocationFields.alternateWebsite];
        delete formLocationFields.alternateWebsite;
      } else if (formLocationFields.alternateWebsite === "") {
        formLocationFields.alternateWebsites = [];
        delete formLocationFields.alternateWebsite;
      }
      // Need to map alternate phones to an array
      if (!!formLocationFields.alternatePhoneNumber) {
        formLocationFields.alternatePhoneNumbers = [formLocationFields.alternatePhoneNumber];
        delete formLocationFields.alternatePhoneNumber;
      } else if (formLocationFields.alternatePhoneNumber === "") {
        formLocationFields.alternatePhoneNumbers = [];
        delete formLocationFields.alternatePhoneNumber;
      }

      // Listing management fields
      const formListingManagementFields = cloneDeep(pickBy(formValues, (value, key) => {
        return !errors[key] && !!touched[key] && listingManagementFields.hasOwnProperty(key)
      }));

      // Business Address fields
      const formBusinessAddressFields = cloneDeep(pickBy(formValues, (value, key) => {
        return !errors[key] && !!touched[key] && businessAddressFields.hasOwnProperty(key)
      }));

      // Need to map year established to an int
      if (!!formListingManagementFields.yearEstablished) {
        formListingManagementFields.yearEstablished = parseInt(formListingManagementFields.yearEstablished);
      }

      const mergeIn = {
        ...formLocationFields,
        listingManagementSpecs: formListingManagementFields,
        businessAddress: formBusinessAddressFields
      };

      mergeIntoLocationPatch(mergeIn);
    } else {
      formInitialized = true;
      // don't want to update the patch until after the form has been initialized
    }
  }, 400);

  const initialPaymentTypes = {};

  !!locationPatch.listingManagementSpecs.paymentTypes ? locationPatch.listingManagementSpecs.paymentTypes.split(",").forEach(paymentType => {
    initialPaymentTypes[paymentType] = true;
  }) : {};

  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState(initialPaymentTypes);

  const mergeInPaymentTypes = (newPaymentTypes) => {
    setSelectedPaymentTypes(newPaymentTypes);
    const serializedPaymentTypes = [];
    Object.keys(newPaymentTypes).forEach(paymentType => {
      if (newPaymentTypes[paymentType]) {
        serializedPaymentTypes.push(paymentType);
      }
    });
    mergeIntoLocationPatch({
      listingManagementSpecs: {
        paymentTypes: (serializedPaymentTypes.length > 0 ? serializedPaymentTypes.join(",") : null)
      }
    })
  };

  const togglePaymentType = (paymentType) => (event) => {
    const checked = event.target.checked;

    if (checked) {
      const newPaymentTypes = {...selectedPaymentTypes, [paymentType]: true};
      mergeInPaymentTypes(newPaymentTypes)
    } else {
      const newPaymentTypes = {...selectedPaymentTypes, [paymentType]: false};
      mergeInPaymentTypes(newPaymentTypes)
    }
  };

  // noinspection RequiredAttributes
  return <div className="card-spacer-top card-spacer-bottom">
    <h5>Listing Details</h5>

    <table className="card-spacer-top" style={{"width": "100%"}}>
      <tbody style={{"width": "100%"}}>
      {!(accountsStore.accounts.length === 1) && <Fragment> {/* Hide account dropdown when there is only one account to select */}
        <tr>
          <td style={{"width": "180px"}}>{/* Set width on first column so everything lines up nicely */}
            Account*
          </td>
          <td>
            <AccountSelector
              multi={false}
              selected={(!!locationPatch.accountId ? [locationPatch.accountId] : [])}
              accountsStore={accountsStore}
              onSelect={_selectAccount}/>
          </td>
        </tr>

        <tr>
          <td/>
          <td>
            {(!locationPatch.accountId && forceGeneralDetailsValidation) ?
              <div className="invalid-feedback" style={{"display": "block"}}>Account is required.</div> :
              <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>
            }
          </td>
        </tr>
      </Fragment>}
        <tr>
          <td style={{"width": "180px"}}>{/* Set width on first column so everything lines up nicely */}
            <label htmlFor="primaryCategory" style={{display: "block"}}>
              Primary Category (Google)*
            </label>
          </td>
          <td>
            <GoogleCategorySelector
              multi={false}
              selected={(!!locationPatch.primaryCategory ? [locationPatch.primaryCategory] : [])}
              categoriesStore={categoriesStore}
              onSelect={handlePrimaryCategoryUpdate}
            />
          </td>
        </tr>
        <tr>
          <td/>
          <td>
            {primaryCategoryAlternate === null
              ? ((!locationPatch.primaryCategory && forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>Primary Category is required.</div> :
                  <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>
              )
              : <div className="invalid-feedback" style={{"display": "block"}}>
                This primary category isn't supported by all listing sites. The closest alternative we've found is{" "}
                <a style={{"textDecoration": "underline"}} onClick={() => handlePrimaryCategoryUpdate(primaryCategoryAlternate)}>
                  {primaryCategoryAlternate}
                </a>
              </div>}
          </td>
        </tr>

    <Formik
      initialValues={initialValues}
      validateOnMount={true}
      validationSchema={BulkLocationEditorGeneralDetailsSchema}
    >
      {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
        }) => {

        handleFormUpdate(values, touched, errors);

        return <Fragment><tr>
              <td>
                <label htmlFor="businessName" style={{display: "block"}}>
                  Business Name*
                </label>
              </td>
              <td>
                <input
                  id="businessName"
                  placeholder="Business Name"
                  type="text"
                  value={values.businessName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    "form-control text-input"
                  }
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td>
                {errors.businessName && (touched.businessName || forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.businessName}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
              </td>
            </tr>


            <tr>
              <td>
                <label htmlFor="primaryWebsite" style={{display: "block"}}>
                  Primary Website{!!combinedLocationServicesMap.listingManagement && "*"}
                </label>
              </td>
              <td>
                <input
                  id="primaryWebsite"
                  placeholder="Primary Website"
                  type="text"
                  value={values.primaryWebsite}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    "form-control text-input"
                  }
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td>
                {errors.primaryWebsite && (touched.primaryWebsite || (!!combinedLocationServicesMap.listingManagement && forceGeneralDetailsValidation)) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.primaryWebsite}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
              </td>
            </tr>


            <tr>
              <td>
                <label htmlFor="alternateWebsite" style={{display: "block"}}>
                  Alternate Website
                </label>
              </td>
              <td>
                <input
                  id="alternateWebsite"
                  placeholder="Alternate Website"
                  type="text"
                  value={values.alternateWebsite}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    "form-control text-input"
                  }
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td>
                {errors.alternateWebsite && (touched.alternateWebsite || forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.alternateWebsite}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
              </td>
            </tr>
          <tr>
            <td>
              <label htmlFor="appointmentUrl" style={{display: "block"}}>
                Appointment URL
              </label>
            </td>
            <td>
                <input
                    type="text"
                    id="AppointmentUrl"
                    placeholder="Appointment URL"
                    maxLength={155}
                    value={locationPatch.listingManagementSpecs.appointmentUrl || ''}
                    onChange={setAppointmentUrl}
                    className="form-control text-input"
                />
            </td>
          </tr>
          <tr>
            <td/>
            <td>
              {errors.appointmentUrl && (touched.appointmentUrl || forceGeneralDetailsValidation) ?
                  <div className="invalid-feedback" style={{"display": "block"}}>{errors.appointmentUrl}</div> :
                  <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
            </td>
          </tr>
        <tr>
          <td>
            <label htmlFor="country" style={{display: "block"}} className={(!singleLocationMode ? " cursor-not-allowed" : "")}>
              Country*
            </label>
          </td>
          <td id="country">
            <CountrySelector
              disabled={!singleLocationMode}
              multi={false}
              selected={(!!locationPatch.businessAddress && !!locationPatch.businessAddress.country ?
                [locationPatch.businessAddress.country]
                : []
              )}
              onSelect={handleCountryUpdate}
            />
          {!singleLocationMode && <UncontrolledTooltip target="country">
            Country can only be edited for single locations.
          </UncontrolledTooltip>}
          </td>
        </tr>
          <tr>
            <td/>
            <td>
              <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>
            </td>
          </tr>
          <tr>
            <td>
              <label htmlFor="street" style={{display: "block"}} className={(!singleLocationMode ? " cursor-not-allowed" : "")}>
                Street Address*
              </label>
            </td>
            <td>
              <input
                disabled={!singleLocationMode}
                id="street"
                placeholder="Street Address"
                type="text"
                value={values.street}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  ("form-control text-input" + (!singleLocationMode ? " cursor-not-allowed" : ""))
                }
              />
              {!singleLocationMode && <UncontrolledTooltip target="street">
                Street can only be edited for single locations.
              </UncontrolledTooltip>}
            </td>
          </tr>
          <tr>
            <td/>
            <td>
              {errors.street && (touched.street || forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.street}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
            </td>
          </tr>

          <tr>
            <td>
              <label htmlFor="suite" style={{display: "block"}} className={(!singleLocationMode ? " cursor-not-allowed" : "")}>
                Suite
              </label>
            </td>
            <td>
              <input
                disabled={!singleLocationMode}
                id="suite"
                placeholder="Suite"
                type="text"
                maxLength="20"
                value={values.suite}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  "form-control text-input"
                  + (!singleLocationMode ? " cursor-not-allowed" : "")
                }
              />
              {!singleLocationMode && <UncontrolledTooltip target="suite">
                Suite can only be edited for single locations.
              </UncontrolledTooltip>}
            </td>
          </tr>
          <tr>
            <td/>
            <td>
              {errors.suite && (touched.suite || forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.suite}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
            </td>
          </tr>

          <tr>
            <td>
              <label htmlFor="city" style={{display: "block"}} className={(!singleLocationMode ? " cursor-not-allowed" : "")}>
                City*
              </label>
            </td>
            <td>
              <input
                disabled={!singleLocationMode}
                id="city"
                placeholder="City"
                type="text"
                value={values.city}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  "form-control text-input"
                  + (!singleLocationMode ? " cursor-not-allowed" : "")
                }
              />
              {!singleLocationMode && <UncontrolledTooltip target="city">
                City can only be edited for single locations.
              </UncontrolledTooltip>}
            </td>
          </tr>
          <tr>
            <td/>
            <td>
              {errors.city && (touched.city || forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.city}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
            </td>
          </tr>

          <tr>
            <td>
              <label htmlFor="state" style={{display: "block"}} className={(!singleLocationMode ? " cursor-not-allowed" : "")}>
                State/Province*
              </label>
            </td>
            <td>
              <input
                disabled={!singleLocationMode}
                id="state"
                placeholder="State/Province"
                type="text"
                maxlength={["US","CA"].includes(locationPatch.businessAddress.country)? 2 : "" }
                value={values.state}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  "form-control text-input"
                  + (!singleLocationMode ? " cursor-not-allowed" : "")
                }
              />
              {!singleLocationMode && <UncontrolledTooltip target="state">
                State can only be edited for single locations.
              </UncontrolledTooltip>}
            </td>
          </tr>
          <tr>
            <td/>
            <td>
              {errors.state && (touched.state || forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.state}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
            </td>
          </tr>

          <tr>
            <td>
              <label htmlFor="postalCode" style={{display: "block"}} className={(!singleLocationMode ? " cursor-not-allowed" : "")}>
                Postal Code*
              </label>
            </td>
            <td>
              <input
                disabled={!singleLocationMode}
                id="postalCode"
                placeholder="Postal Code"
                type="text"
                value={values.postalCode}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  "form-control text-input"
                  + (!singleLocationMode ? " cursor-not-allowed" : "")
                }
              />
              {!singleLocationMode && <UncontrolledTooltip target="postalCode">
                Postal Code can only be edited for single locations.
              </UncontrolledTooltip>}
            </td>
          </tr>
          <tr>
            <td/>
            <td>
              {errors.postalCode && (touched.postalCode || forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.postalCode}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
            </td>
          </tr>

          <tr>
            <td>
              <label htmlFor="primaryPhoneNumber" style={{display: "block"}} className={(!singleLocationMode ? " cursor-not-allowed" : "")}>
                Main Phone*
              </label>
            </td>
            <td>
              <input
                disabled={!singleLocationMode}
                id="primaryPhoneNumber"
                placeholder="Main Phone"
                type="text"
                value={values.primaryPhoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  "form-control text-input"
                  + (!singleLocationMode ? " cursor-not-allowed" : "")
                }
              />
              {!singleLocationMode && <UncontrolledTooltip target="primaryPhoneNumber">
                Main Phone can only be edited for single locations.
              </UncontrolledTooltip>}
            </td>
          </tr>
          <tr>
            <td/>
            <td>
              {errors.primaryPhoneNumber && (touched.primaryPhoneNumber || forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.primaryPhoneNumber}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
            </td>
          </tr>


          <tr>
            <td>
              <label htmlFor="alternatePhoneNumber" style={{display: "block"}} className={(!singleLocationMode ? " cursor-not-allowed" : "")}>
                Alternate Phone Number
              </label>
            </td>
            <td>
              <input
                disabled={!singleLocationMode}
                id="alternatePhoneNumber"
                placeholder="Alternate Phone Number"
                type="text"
                value={values.alternatePhoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  "form-control text-input"
                  + (!singleLocationMode ? " cursor-not-allowed" : "")
                }
              />
              {!singleLocationMode && <UncontrolledTooltip target="alternatePhoneNumber">
                Alternate Phone Number can only be edited for single locations.
              </UncontrolledTooltip>}
            </td>
          </tr>
          <tr>
            <td/>
            <td>
              {errors.alternatePhoneNumber && (touched.alternatePhoneNumber || forceGeneralDetailsValidation) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.alternatePhoneNumber}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
            </td>
          </tr>

          {!!combinedLocationServicesMap.listingManagement && <Fragment>
            <tr>
              <td>
                <label htmlFor="description" style={{display: "block"}}>
                  Business Description*
                </label>
              </td>
              <td>
                <textarea
                  id="description"
                  placeholder="Business Description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    "form-control text-input"
                  }
                />
              </td>
            </tr>
            <tr>
              <td />
              <td>
                {errors.description && (touched.description || forceGeneralDetailsValidation) ?
                  <div className="pull-left invalid-feedback" style={{"display": "block", width: "auto"}}>{errors.description}</div> :
                  <div className="pull-left invalid-feedback" style={{"display": "block", width: "auto", opacity: "0"}}>line spacer</div>}
                <small className="pull-right ng-binding">{!!values.description ? <span>{values.description.length}</span> : <span style={{"display": "block", opacity: "0"}}>line spacer</span>}</small>
              </td>
            </tr>
          </Fragment>}


          {!!combinedLocationServicesMap.listingManagement && <Fragment>
            <tr>
              <td>
                <label htmlFor="businessOwner" style={{display: "block"}}>
                  Business Owner
                </label>
              </td>
              <td>
                <input
                  id="businessOwner"
                  placeholder="Business Owner"
                  type="text"
                  value={values.businessOwner}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    "form-control text-input"
                  }
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td>
                {errors.businessOwner && (touched.businessOwner) ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.businessOwner}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
              </td>
            </tr>
          </Fragment>}

          {!!combinedLocationServicesMap.listingManagement && <Fragment>
            <tr>
              <td>
                <label htmlFor="businessEmail" style={{display: "block"}}>
                  Business Email
                </label>
              </td>
              <td>
                <input
                  id="businessEmail"
                  placeholder="Business Email"
                  type="text"
                  value={values.businessEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    "form-control text-input"
                  }
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td>
                {errors.businessEmail && touched.businessEmail ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.businessEmail}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
              </td>
            </tr>
          </Fragment>}


          {!!combinedLocationServicesMap.listingManagement && <Fragment>
            <tr>
              <td>
                <label htmlFor="faxNumber" style={{display: "block"}}>
                  Business Fax
                </label>
              </td>
              <td>
                <input
                  id="faxNumber"
                  placeholder="Business Fax"
                  type="text"
                  value={values.faxNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    "form-control text-input"
                  }
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td>
                {errors.faxNumber && touched.faxNumber ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.faxNumber}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
              </td>
            </tr>
          </Fragment>}


          {!!combinedLocationServicesMap.listingManagement && <Fragment>
            <tr>
              <td>
                <label htmlFor="yearEstablished" style={{display: "block"}}>
                  Year Established
                </label>
              </td>
              <td>
                <input
                  id="yearEstablished"
                  placeholder="Year Established"
                  type="text"
                  value={values.yearEstablished}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    "form-control text-input"
                  }
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td>
                {errors.yearEstablished && touched.yearEstablished ?
                <div className="invalid-feedback" style={{"display": "block"}}>{errors.yearEstablished}</div> :
                <div className="invalid-feedback" style={{"display": "block", opacity: "0"}}>line spacer</div>}
              </td>
            </tr>
            <tr>
              <td>
                <label style={{display: "block"}}>
                  Payment Types
                </label>
              </td>
              <td>
                {paymentTypes.map((paymentType, index) => {
                  return <div className="custom-control custom-checkbox control-spacer-left">
                    <input type="checkbox" id={"paymenttype-" + index} className="custom-control-input" checked={!!selectedPaymentTypes[paymentType]} onChange={togglePaymentType(paymentType)} />
                    <label className="custom-control-label" htmlFor={"paymenttype-" + index}>{paymentType}</label>
                  </div>
                })}
              </td>
            </tr>
          </Fragment>}
        </Fragment>
      }
      }
    </Formik>
      </tbody>
    </table>
  </div>
}

export default BulkLocationEditorGeneralDetails;
